import { useSelector } from 'react-redux';
import { useRevalidator } from 'react-router';
import { useSearchParams } from '../../../common/hooks/useSearchParams';
import { getCompanyName } from '../../../common/utils/accountUtils.js';
import { convertStringMapToCommonErrors } from '../../../common/utils/errorUtils.js';
import { deepEqual } from '../../../common/utils/objectUtils.js';
import { getContactInfo } from '../../../common/utils/stateUtils.js';
import { Loading } from '../../../components/Loading/Loading.js';
import { useAuth } from '../../../public/site/AuthProvider.js';
import { getActiveAccountMasterId } from '../../../selfservice/common/localStorageUtils';
import { DeviceCheckoutContactInfo } from '../../DeviceCheckoutContactInfo/DeviceCheckoutContactInfo.js';
import { MigrationLogin } from '../../Login/MigrationLogin';
import { LoginAndRegistration } from '../../LoginAndRegistration/LoginAndRegistration.js';
import type { State } from '../../../selfservice/common/store.js';
import type { FunctionComponent } from 'react';

interface CheckoutLoginOrContactInfoStepProps {
  onNext?: () => void;
}

export const CheckoutLoginOrContactInfoStep: FunctionComponent<CheckoutLoginOrContactInfoStepProps> = (
  props: CheckoutLoginOrContactInfoStepProps
) => {
  const cartItems = useSelector((state: State) => state.deviceCheckout?.cartItems ?? [], deepEqual);
  const { authenticatedUser, ssoSessionValid, isLoading, updateUserDetails, inMigrationPhase } = useAuth();
  const { revalidate } = useRevalidator();
  const { mdmId } = useSearchParams<{ mdmId?: string }>();

  const companyName = getCompanyName(authenticatedUser, mdmId);
  const loginView = inMigrationPhase ? (
    <MigrationLogin includeAdmins={true} compact={true} />
  ) : (
    <LoginAndRegistration />
  );

  if (ssoSessionValid && authenticatedUser) {
    return (
      <DeviceCheckoutContactInfo
        cartItems={cartItems}
        contactInfo={getContactInfo(authenticatedUser)}
        companyName={companyName || getCompanyName(authenticatedUser, getActiveAccountMasterId())}
        user={authenticatedUser}
        saveContactInfoAndOpenNextSection={(contactInfo, validationErrors) => {
          updateUserDetails(contactInfo, undefined, convertStringMapToCommonErrors(validationErrors));
          revalidate();
          if (props.onNext) {
            props.onNext();
          }
        }}
      />
    );
  }

  if (isLoading) {
    return <Loading big={true} />;
  }

  return loginView;
};
